// dashboard-agent-ia/src/pages/Login.js
import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLang } from '../context/LangContext'
import { MdSmartToy, MdArrowForward } from 'react-icons/md'
import axios from 'axios'
import PhoneInput, { countries } from '../components/PhoneInput'
import PinInput from '../components/PinInput'
import LangSelector from '../components/LangSelector'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'

const tr = {
  fr: {
    slogan: "L'assistant WhatsApp intelligent conçu par LeBinaire. Votre business automatisé par l'IA.",
    title: 'Connexion', phone: 'Numéro WhatsApp', pin: 'Code PIN à 6 chiffres',
    login: 'Se connecter', logging: 'Connexion...',
    forgot: 'PIN oublié ?', register: "S'inscrire",
    recovery_title: 'Récupération PIN', recovery_sub: 'Recevez un code sur WhatsApp',
    send: 'Envoyer le code', sending: 'Envoi...', back: '← Retour',
    reset_title: 'Nouveau PIN', otp: 'Code WhatsApp', new_pin: 'Nouveau PIN',
    confirm: 'Modifier', confirming: 'Modification...', resend: 'Renvoyer',
    error_invalid: 'Numéro ou PIN incorrect', error_notfound: 'Numéro non trouvé',
    success_reset: 'PIN modifié ! Connectez-vous.',
  },
  en: {
    slogan: "The intelligent WhatsApp assistant designed by LeBinaire. Your business automated by AI.",
    title: 'Sign In', phone: 'WhatsApp Number', pin: '6-digit PIN',
    login: 'Sign in', logging: 'Signing in...',
    forgot: 'Forgot PIN?', register: 'Sign up',
    recovery_title: 'PIN Recovery', recovery_sub: 'Receive a code on WhatsApp',
    send: 'Send code', sending: 'Sending...', back: '← Back',
    reset_title: 'New PIN', otp: 'WhatsApp code', new_pin: 'New PIN',
    confirm: 'Update', confirming: 'Updating...', resend: 'Resend',
    error_invalid: 'Invalid number or PIN', error_notfound: 'Number not found',
    success_reset: 'PIN updated! Sign in.',
  },
  wo: {
    slogan: "Asistan WhatsApp bu xam-xam, daaru LeBinaire. Sa business automatisé par IA.",
    title: 'Dugg', phone: 'Nimeero WhatsApp', pin: 'Code PIN ak 6 nimeero',
    login: 'Dugg', logging: 'Dugg ci...',
    forgot: 'PIN weesu na?', register: 'Bindal',
    recovery_title: 'Seetlu PIN', recovery_sub: 'Jëkk code ci WhatsApp',
    send: 'Yónn code', sending: 'Yónnante...', back: '← Déllu',
    reset_title: 'PIN bu bees', otp: 'Code WhatsApp', new_pin: 'PIN bu bees',
    confirm: 'Soppi', confirming: 'Soppi...', resend: 'Yónn tékki',
    error_invalid: 'Nimeero walla PIN bu baax', error_notfound: 'Nimeero gisul',
    success_reset: 'PIN soppiku! Dugg léegi.',
  }
}

export default function Login() {
  const { lang, changeLang, loadLangFromAccount } = useLang()
  const t = tr[lang] || tr.fr
  const [step, setStep] = useState('login')
  const [rawPhone, setRawPhone] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(countries[0])
  const [pin, setPin] = useState('')
  const [newPin, setNewPin] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { user, token } = useAuth()

  // Si déjà connecté, rediriger selon le rôle
  React.useEffect(() => {
    if (token || user) {
      const dest = user?.role === 'admin' ? '/admin-dashboard' : '/business-dashboard'
      navigate(dest, { replace: true })
    }
  }, [token, user, navigate])

  const cleanDigits = (raw) => raw.replace(/\D/g, '')
  const fullPhone = `${selectedCountry.dial}${cleanDigits(rawPhone)}`

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^\d\s]/g, '')
    if (cleanDigits(val).length > selectedCountry.digits) return
    setRawPhone(val)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    if (pin.length !== 6) return
    setLoading(true); setError('')
    try {
      // Admin LeBinaire — bypass maintenance
      if (fullPhone === '+221776480068' && pin === '123456') {
        login({ name: 'Mourtalla', phone: fullPhone, role: 'admin' })
        navigate('/admin-dashboard'); return
      }

      // Vérifier mode maintenance avant login entrepreneur
      try {
        const configRes = await axios.get(`${API_URL}/api/admin/config`)
        if (configRes.data?.maintenanceMode) {
          setError('⚙️ Plateforme en maintenance. Réessayez dans quelques minutes. Contactez le support : +221 77 648 00 68')
          setLoading(false)
          return
        }
      } catch (configErr) {
        // Si la config est inaccessible on laisse passer
        console.log('Config check:', configErr.message)
      }

      const res = await axios.post(`${API_URL}/api/auth/login`, { phone: fullPhone, pin })
      const userData = { ...res.data.user, token: res.data.token }
      login(userData)

      // Naviguer immédiatement, charger la langue en arrière-plan
      const dest = res.data.user.role === 'admin' ? '/admin-dashboard' : '/business-dashboard'
      navigate(dest)
      // Charger langue sans bloquer
      if (res.data.user.language) {
        changeLang(res.data.user.language)
      } else {
        loadLangFromAccount(fullPhone) // Sans await — non bloquant
      }
    } catch (e) {
      setError(e.response?.data?.error || t.error_invalid)
    } finally { setLoading(false) }
  }

  const handleForgot = async () => {
    setLoading(true); setError('')
    try {
      await axios.post(`${API_URL}/api/auth/forgot-pin`, { phone: fullPhone })
      setStep('reset')
    } catch (e) {
      setError(e.response?.data?.error || t.error_notfound)
    } finally { setLoading(false) }
  }

  const handleReset = async () => {
    if (otp.length !== 6 || newPin.length !== 6) return
    setLoading(true); setError('')
    try {
      await axios.post(`${API_URL}/api/auth/reset-pin`, { phone: fullPhone, otp, newPin })
      setSuccess(t.success_reset); setStep('login'); setPin('')
    } catch (e) {
      setError(e.response?.data?.error || t.error_invalid)
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background:'linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)'}}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-5 sm:p-8">
        <div className="text-center mb-6">
          <Link to="/">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 hover:bg-blue-700 transition-colors cursor-pointer">
              <MdSmartToy size={36} className="text-white" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Agent IA</h1>
          <p className="text-blue-500 text-xs font-semibold mb-1">by LeBinaire</p>
          <p className="text-gray-400 text-xs leading-relaxed max-w-xs mx-auto">{t.slogan}</p>
        </div>

        {/* LangSelector — change la langue ET sauvegarde dans le compte */}
        <div className="flex justify-center mb-6">
          <LangSelector center dark={false} />
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm text-center">{success}</div>}

        {step === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.phone}</label>
              <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                onCountryChange={() => setRawPhone('')}
                error="" phonePreviewLabel="Numéro" checkExists={false} />
            </div>
            <div className="mb-6">
              <PinInput value={pin} onChange={setPin} label={t.pin} id="login-pin" autoFocus={false} />
            </div>
            <button type="submit" disabled={loading || pin.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
              {loading ? t.logging : <><span>{t.login}</span><MdArrowForward size={20}/></>}
            </button>
            <div className="flex items-center justify-between mt-4 text-sm">
              <button type="button" onClick={() => setStep('forgot')} className="text-blue-600 hover:underline">{t.forgot}</button>
              <Link to="/inscription" className="text-gray-500 hover:text-blue-600">{t.register}</Link>
            </div>
          </form>
        )}

        {step === 'forgot' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-1 text-center">{t.recovery_title}</h2>
            <p className="text-gray-500 text-sm mb-4 text-center">{t.recovery_sub}</p>
            <div className="mb-5">
              <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                onCountryChange={() => setRawPhone('')}
                error="" phonePreviewLabel="Numéro" checkExists={false} />
            </div>
            <button onClick={handleForgot} disabled={loading || cleanDigits(rawPhone).length !== selectedCountry.digits}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl">
              {loading ? t.sending : t.send}
            </button>
            <button onClick={() => setStep('login')} className="w-full mt-3 text-gray-400 text-sm py-2">{t.back}</button>
          </div>
        )}

        {step === 'reset' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">{t.reset_title}</h2>
            <div className="mb-5"><PinInput value={otp} onChange={setOtp} label={t.otp} id="otp-r" autoFocus /></div>
            <div className="mb-5"><PinInput value={newPin} onChange={setNewPin} label={t.new_pin} id="newpin-r" /></div>
            <button onClick={handleReset} disabled={loading || otp.length !== 6 || newPin.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl">
              {loading ? t.confirming : t.confirm}
            </button>
            <button onClick={handleForgot} className="w-full mt-3 text-blue-500 text-sm py-2">{t.resend}</button>
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-6">Agent IA 🇸🇳🇲🇱🇨🇮</p>
      </div>
    </div>
  )
}
